<template>
  <label
    :class="[
      'q-checkbox',
      `q-checkbox--${size}`,
      {
        'q-checkbox--disabled': disabled,
        'q-checkbox--hover': hover,
        'q-checkbox--selected': modelValue,
      },
    ]"
    :style="{ opacity: disabled ? 0.25 : 1 }"
  >
    <input
      type="checkbox"
      :checked="modelValue"
      :disabled="disabled"
      class="q-checkbox__input"
      @change="$emit('update:modelValue', $event.target.checked)"
      @click="$emit('click', $event)"
    />

    <div class="q-checkbox__box">
      <svg
        v-if="modelValue"
        class="q-checkbox__icon"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M20.25 6.75047L9.75 17.25L4.5 12.0005"
          stroke="#FFFFFF"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>

    <span class="q-checkbox__text">
      <slot />
    </span>
  </label>
</template>

<script setup>
defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'l',
    validator: (value) => ['l', 's'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hover: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['update:modelValue', 'click']);
</script>

<style lang="scss" scoped>
.q-checkbox {
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  user-select: none;

  &--s {
    gap: 8px;
  }

  &__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  &__box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background-color: rgba(14, 14, 34, 0.1);
    transition: all 0.2s ease;

    .q-checkbox--s & {
      width: 16px;
      height: 16px;
      border-radius: 4px;
    }
  }

  &__icon {
    width: 24px;
    height: 24px;

    .q-checkbox--s & {
      width: 16px;
      height: 16px;
    }
  }

  &__text {
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 1.2;
    color: rgba(14, 14, 34, 0.8);

    .q-checkbox--s & {
      font-size: 14px;
    }
  }

  // Selected state
  &--selected {
    .q-checkbox__box {
      background-color: #6ca1f8;
    }

    .q-checkbox__text {
      color: rgba(14, 14, 34, 0.8);
    }
  }

  // Hover states
  &:hover:not(.q-checkbox--disabled),
  &--hover:not(.q-checkbox--disabled) {
    .q-checkbox__box {
      background-color: rgba(14, 14, 34, 0.2);
    }

    .q-checkbox__text {
      color: rgba(14, 14, 34, 0.9);
    }

    &.q-checkbox--selected {
      .q-checkbox__box {
        background-color: #3b82f6;
      }
    }
  }

  // Disabled state
  &--disabled {
    cursor: not-allowed;

    .q-checkbox__box {
      background-color: rgba(14, 14, 34, 0.1);
    }

    .q-checkbox__text {
      color: rgba(14, 14, 34, 0.8);
    }
  }
}
</style>
